<template>
  <nav
    :aria-label="$t.primaryNavigationLabel"
    class="relative h-full flex"
    :class="{ 'animate-filter-backdrop-blur-in': blurAnimationReady }"
  >
    <!-- L1 Section -->
    <section
      v-style:w="{ sm: '100%', lg: '23.4375rem' }"
      class="f-col shrink-0 overflow-auto p-4 lg:overflow-x-hidden lg:ws-nowrap"
      :class="{ '<lg:hidden': activeL1.id }"
    >
      <div class="mb-6 flex c-black between">
        <base-link to="/">
          <span class="sr-only">{{ $t.brand }} {{ $t.home }}</span>
          <vf-logo variant="full" />
        </base-link>
        <transition name="fade">
          <base-button
            v-if="!activeL1?.id"
            :aria-label="$t.close"
            class="p-2 -m-2"
            data-test-id="vf-dialog-close"
            @click="emit('close')"
          >
            <vf-icon name="close" size="md" />
          </base-button>
        </transition>
      </div>

      <section class="relative b-b b-brand-1/20 px-4 pb-4 text-sm c-brand-4">
        <div class="flex items-center between -mx-4">
          <div>
            <div class="subtitle-4 c-brand-4">
              {{ greeting }}
            </div>
            <div v-if="auth.loggedIn">
              <span v-if="$feature.enableLoyaltyFeatures">
                <template v-if="user.isLoyaltyMember">
                  <template
                    v-if="!loyalty.vouchersPending
                      && !loyalty.vouchers?.applicableLoyaltyVouchers?.length && user.loyaltyPointsBalance"
                  >
                    {{ user.loyaltyPointsBalance }} {{ $pluralize($t.points, user.loyaltyPointsBalance) }}
                  </template>
                  <template v-else-if="loyalty.vouchers?.applicableLoyaltyVouchers?.[0]?.available">
                    {{ replaceAll($t.loyaltyRewardsDetails.rewardAmount, { reward: loyalty.currentReward }) }}
                  </template>
                </template>
                <span v-else>{{ $t.joinLoyaltyCta }}</span>
              </span>
            </div>
            <base-interpolator v-else :content="$t.signInOrCreateAccount">
              <template #sign-in="{ args: [message] }">
                <base-button
                  class="underlined"
                  data-test-id="checkout-sign-in-button"
                  @click="DialogSignIn.open({ formLocation: 'modal:single:checkout:none' })"
                >
                  {{ message }}
                </base-button>
              </template>
              <template #join-now="{ args: [message] }">
                <base-button
                  class="underlined"
                  data-test-id="checkout-sign-up-button"
                  @click="DialogSignUp.open({ formLocation: 'modal:single:checkout:none' })"
                >
                  {{ message }}
                </base-button>
              </template>
            </base-interpolator>
          </div>
          <template v-if="auth.loggedIn">
            <base-link
              v-if="!$feature.enableLoyaltyFeatures || user.isLoyaltyMember"
              :aria-label="$t.myAccount"
              :to="$feature.enableLoyaltyFeatures ? '/account/loyalty' : '/account'"
            >
              <vf-icon class="c-brand-4" name="account" size="md" />
              <span class="absolute-0" />
            </base-link>
            <base-button
              v-else
              :aria-label="$t.joinLoyaltyCta"
              @click="DialogLoyaltySignUp.open({ formLocation: 'modal:single:myaccount:none' })"
            >
              <vf-icon class="c-brand-4" name="account" size="md" />
              <span class="absolute-0" />
            </base-button>
          </template>
          <vf-icon v-else class="c-brand-4" name="account" size="md" />
        </div>
      </section>

      <transition-group class="f-col grow -mx-4" name="stagger-slide" tag="ul">
        <li
          v-for="(l1, i) in items.children"
          :key="l1.id || i"
          :ref="el => l1Refs[l1.id] = el"
          class="@hover:bg-brand-1 @hover:bg-op-10"
          :class="{ 'o:bg-op-20': l1.id === activeL1?.id }"
          :style="getStaggerStyle(i)"
        >
          <cms-section
            :id="l1.id"
            class="h-full children:h-full"
            :section="{ name: l1.id, items: [{ ...l1 }] }"
          />
        </li>
      </transition-group>

      <vf-utility-navigation
        v-if="links"
        class="mt-4"
        :links
        @click="emit('close')"
        @sign-in="emit('signIn')"
      />
    </section>

    <!-- L2/L3 Sections -->
    <section
      v-if="activeL1.id"
      ref="l2SectionRef"
      v-style:w="{ sm: '100%', lg: '23.4375rem' }"
      class="shrink-0 overflow-auto p-4 lg:b-l lg:b-brand-4/20"
    >
      <div class="mb-6 flex c-black between lg:justify-end">
        <base-button ref="backButtonRef" class="flex items-center -ml-2" @click="handleBackClick">
          <vf-icon class="lg:hidden" dir="left" name="chevron" size="md" />
          <vf-icon class="<lg:hidden" name="close" size="md" />
          <span class="sr-only">{{ $t.backTo }}</span>
          <span class="body-2 lg:sr-only" style="margin-top: -0.0625rem">{{ activeL1.title }}</span>
        </base-button>
        <base-button
          :aria-label="$t.close"
          class="p-2 -m-2 lg:hidden"
          data-test-id="vf-dialog-close"
          @click="emit('close')"
        >
          <vf-icon name="close" size="md" />
        </base-button>
      </div>

      <div class="<lg:-mx-4">
        <div v-if="activeL1.id" class="slide-left w-full">
          <cms-section
            v-if="activeL1.visualSelectors"
            :section="{ items: activeL1.visualSelectors, name: 'visual-selectors' }"
            :style="getStaggerStyle(0)"
          />
          <ul>
            <li
              class="b-b b-brand-1/20"
              :class="{ '<lg:hidden': activeL1.hideShopAll?.sm, 'lg:hidden': activeL1.hideShopAll?.lg }"
              :style="getStaggerStyle(0)"
            >
              <base-link
                class="w-full flex items-center py-3 between <lg:px-4"
                rel="noopener nofollow"
                :to="activeL1.target?.url"
                @click="handleL1ViewAllClick(activeL1 as MenuItem)"
              >
                <span class="subtitle-4">{{ activeL1.overrideShopAll || `${$t.viewAll} ${activeL1.title}` }}</span>
              </base-link>
            </li>
            <template v-for="(column, i) in activeL1.columns" :key="i">
              <li v-for="(l2) in column?.children" :key="l2?.id">
                <cms-section
                  class="b-b b-brand-1/20"
                  :section="{ name: l2?.id, items: [{ ...l2, menuId: activeL1.id }] }"
                  :style="getStaggerStyle(i + 1)"
                />
              </li>
            </template>
          </ul>
        </div>
      </div>
    </section>
  </nav>
</template>

<script lang="ts" setup>
import type { MegaMenuContent, MenuItem } from '#types/components/cms/mega-menu'
import type { NavigationLink } from '#types/navigation'
import type { RecursivePartial } from '#types/utils'
import { BaseButton, BaseLink } from '#components'
import { MegaMenuL1ContextKey } from '#content/contexts/megamenu'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

const { items } = defineProps<{
  items: MegaMenuContent
  links?: NavigationLink[]
}>()

const emit = defineEmits<{
  close: []
  signIn: []
}>()

const { DialogSignIn, DialogSignUp, DialogLoyaltySignUp } = useDialogsStore()

const auth = useAuthStore()
const loyalty = useLoyaltyStore()
const { $feature, $gtm, $t } = useNuxtApp()
const backButtonRef = useTemplateRef('backButtonRef')
const l2SectionRef = useTemplateRef<HTMLElement>('l2SectionRef')
const user = useUserStore()

const activeL1 = ref<RecursivePartial<MenuItem>>({})
const blurAnimationReady = ref(false)
const expandedL2 = ref<string[]>([])
const l1Refs = ref({})
const l2Refs = ref({})

provide(MegaMenuL1ContextKey, { activeL1, expandedL2, l2Refs })

const greeting = computed(() => {
  if (auth.loggedIn) {
    return user.details?.firstName
      ? replaceAll($t.loyaltyRewardsDetails.greetingWithName, { name: user.details.firstName })
      : $t.loyaltyRewardsDetails.greeting
  }

  if ($feature.enableLoyaltyFeatures) {
    return $t.joinLoyaltyCta
  }

  return $t.signIn
})

const getStaggerStyle = (index: number, direction: 'x' | 'y' = 'x') => {
  const delay = index * 50
  const transform = direction === 'x' ? 'translateX(-30px)' : 'translateY(-20px)'

  return {
    '--stagger-delay': `${delay}ms`,
    'opacity': '0',
    transform,
    'animation': 'vf-stagger-fade-in 0.6s ease forwards',
    'animationDelay': `${delay}ms`
  }
}

const focusL1Button = (id: string) => nextTick(() => l1Refs.value[id]?.querySelector('button')?.focus())

const handleBackClick = () => {
  const l1 = activeL1.value
  if (l1.id) focusL1Button(l1.id)
  activeL1.value = {}
}

const handleL1ViewAllClick = (l1: MenuItem) => {
  $gtm.push('topNav.onClickTopNavLink', formatMegaMenuLevelLabel(getMegaMenuItemLabel(l1), 1))
  emit('close')
}

const { activate: activateL2FocusTrap, deactivate: deactivateL2FocusTrap } = useFocusTrap(l2SectionRef, {
  escapeDeactivates: false,
  clickOutsideDeactivates: true,
  allowOutsideClick: true,
  returnFocusOnDeactivate: false,
  initialFocus: () => backButtonRef.value?.$el
})

watch(() => activeL1.value.id, (id) => {
  if (id) nextTick(activateL2FocusTrap)
  else deactivateL2FocusTrap()
})

useEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Escape' && activeL1.value.id) {
    e.preventDefault()
    handleBackClick()
  }
}, { capture: true })

onMounted(() => {
  if (auth.loggedIn && !loyalty?.vouchers) loyalty.getVouchers()
  setTimeout(() => blurAnimationReady.value = true, 300)
})
</script>
